import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseViewComponent } from '@sb/library/base';
import { CalendarBookingDisplayItem, TagDisplayItem, TeacherNameDisplayItem, WebsiteRenderContextTeacherNameDisplayItem } from '@sb/library/data-models';
import { PreFetchedSlots } from '../signup-form.definitions';

@Component({
    selector: 'lib-signup-form-design-block-subjects',
    templateUrl: './signup-form-design-block-subjects.component.html',
    styleUrl: './signup-form-design-block-subjects.component.scss'
})
export class SignupFormDesignBlockSubjectsComponent extends BaseViewComponent implements AfterViewInit {

    @Input() teachersBySubjectMap: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};
    @Input() teachersByCategoryNameMap: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};
    @Input() bookingSlots: CalendarBookingDisplayItem[] = [];
    @Input() totalItemsLength: number = 0;
    @Input() existingEvents!: boolean;
    @Input() subjectTags: TagDisplayItem[] = [];

    @Input() isViewOnly: boolean = false;
    @Output() signupClicked: EventEmitter<PreFetchedSlots> = new EventEmitter();

    private _itemSelected: string | null = null;
    private _clicked: boolean = false;
    private _visibleItemsCount: number = 4;
    private _availableBookingSlots: CalendarBookingDisplayItem[] = [];

    private _sortedSubjects: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};
    private _sortedCategories: Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> = {};

    ngAfterViewInit(): void {
        if (this.existingEvents) {
            const categoriesByNumberOfTeachers = Object.entries(this.teachersByCategoryNameMap).sort((a,b) => {
                const countDiff = b[1].length - a[1].length;
                if (countDiff !== 0) {
                    return countDiff;
                }
                return a[0].localeCompare(b[0]);
            });
            this._sortedCategories = Object.fromEntries(categoriesByNumberOfTeachers);
        } else {
            const subjectsByNumberOfTeachers = Object.entries(this.teachersBySubjectMap).sort(([keyA, valA], [keyB, valB]) => {
                // Sorting rules:
                // Primary: "Other" key is the last one
                if (keyA === 'Other') return 1;
                if (keyB === 'Other') return -1;
                
                // Secondary: sort by number of teachers descending (b[1].length - a[1].length)
                const countDiff = valB.length - valA.length;
                if (countDiff !== 0) return countDiff;

                // Tertiary: sort alphabetically
                return keyA.localeCompare(keyB);
            });
            this._sortedSubjects = Object.fromEntries(subjectsByNumberOfTeachers);
        }
    }

    ItemSelected(subject: string): void {
        this._itemSelected = subject;
        this.Signup();
    }

    Signup(): void {
        if (this.existingEvents) {
            this._availableBookingSlots = [...this.bookingSlots].filter(slot => this.teachersByCategoryNameMap[this._itemSelected!].some(teacher => teacher.ID === slot.OriginalTeacherID) && slot.EventCategory?.Name === this._itemSelected!);
        } else {
            this._availableBookingSlots = [...this.bookingSlots].filter(slot => this.teachersBySubjectMap[this._itemSelected!].some(teacher => teacher.ID === slot.TeacherID));
        }
        this.signupClicked.emit({
            SubjectTag: !this.existingEvents ? this.subjectTags.find(tag => tag.Name == this._itemSelected!)! : undefined,
            BookingSlots: this._availableBookingSlots,
            Category: this.existingEvents ? this._itemSelected! : undefined
        });
        this._clicked = true;
    }

    Reset(): void {
        this._clicked = false;
        this._itemSelected = null;
    }

    ShowMoreItems(): void {
        this._visibleItemsCount = this.totalItemsLength;
    }

    GetMoreTeacherTooltipText(teachers: WebsiteRenderContextTeacherNameDisplayItem[]): string {
        // First 2 teachers are showing on the page, so exclude them on the tooltip text
        return teachers.slice(2).map(teacher => teacher.Name).join(', ');
    }

    get items(): Record<string, WebsiteRenderContextTeacherNameDisplayItem[]> {
        return this.existingEvents ? this._sortedCategories : this._sortedSubjects;
    }

    get itemSelected(): string | null {
        return this._itemSelected;
    }

    get clicked(): boolean {
        return this._clicked;
    }

    get visibleItemsCount(): number {
        return this._visibleItemsCount;
    }

}
